import React from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Navigation from '../components/Navigation';
import AdvancedAnalysis from '../components/AdvancedAnalysis';
import CompetitorAnalysis from '../components/CompetitorAnalysis'; 
import TrendingAnalysis from '../components/TrendingAnalysis';
import StandaloneScriptGenerator from '../components/StandaloneScriptGenerator';
import CommentMining from '../components/CommentMining';
import DashboardLaunchpad from '../components/DashboardLaunchpad';
import NicheResearch from '../components/NicheResearch';
import UploadOptimizer from '../components/UploadOptimizer';
import TagGapAnalysis from '../components/TagGapAnalysis';
import RevenueEstimator from '../components/RevenueEstimator';
import VideoAuditDashboard from './VideoAuditDashboard';
import AdminObservability from './AdminObservability';
import AnalysisHistory from './AnalysisHistory';
import '../styles/ToolPage.css';

export default function ToolPage({ user, onLogout }) {
  const location = useLocation();

  const handleUpdateCredits = (newBalance) => {
    const updatedUser = { ...user, credits: newBalance };
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };

  // Determine which nav item should be highlighted based on URL
  const getCurrentPage = () => {
    if (location.pathname.includes('/hub')) return 'hub';
    if (location.pathname.includes('/niche-research')) return 'niche-research';
    if (location.pathname.includes('/upload-optimizer')) return 'upload-optimizer';
    if (location.pathname.includes('/tag-gap')) return 'tag-gap';
    if (location.pathname.includes('/revenue-estimator')) return 'revenue-estimator';
    if (location.pathname.includes('/comment-mining')) return 'comment-mining';
    if (location.pathname.includes('/competitor')) return 'competitor';
    if (location.pathname.includes('/trending')) return 'trending';
    if (location.pathname.includes('/script-generator')) return 'script-generator';
    if (location.pathname.includes('/keyword-analysis')) return 'analysis';
    if (location.pathname.includes('/history')) return 'history';
    if (location.pathname.includes('/admin/observability')) return 'observability';
    if (location.pathname.includes('/video-audit')) return 'video-audit';
    return 'hub';
  };

  return (
    <div className="tool-page" style={{ background: '#090a0f', minHeight: '100vh', color: '#f8fafc' }}>
      {/* Header */}
      <header className="tool-header" style={{ background: 'rgba(15, 17, 26, 0.9)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '1rem 2rem' }}>
        <div className="header-left" style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <a href="https://contentserp.in/dashboard" className="back-link" style={{ color: '#94a3b8', textDecoration: 'none', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            ← Back to Dashboard
          </a>
          <div className="header-title">
            <h1 style={{ fontSize: '1.6rem', fontWeight: 800, background: 'linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: 0 }}>
              ⚡ ContentSERP Intelligence Engine
            </h1>
            <p className="tagline" style={{ margin: 0, fontSize: '0.82rem', color: '#94a3b8' }}>
              Enterprise YouTube Analytics & AI Recommendation Platform
            </p>
          </div>
        </div>

        <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div className="user-badge" style={{ display: 'flex', alignItems: 'center', gap: '1rem', background: 'rgba(255,255,255,0.03)', padding: '0.4rem 1rem', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)' }}>
            <div className="user-details" style={{ display: 'flex', flexDirection: 'column', textAlign: 'right' }}>
              <span className="user-email" style={{ fontSize: '0.85rem', fontWeight: 600, color: '#f8fafc' }}>{user?.email}</span>
              <span className="user-name" style={{ fontSize: '0.75rem', color: '#94a3b8' }}>👤 {user?.name || 'User'} ({user?.role || 'user'})</span>
            </div>
            <div className="credit-badge" style={{ background: 'linear-gradient(90deg, rgba(139,92,246,0.2), rgba(236,72,153,0.2))', padding: '0.3rem 0.8rem', borderRadius: '8px', border: '1px solid rgba(236,72,153,0.3)', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span className="credit-icon">💳</span>
              <span className="credit-value" style={{ fontWeight: 700, color: '#ec4899', fontSize: '0.95rem' }}>{user?.credits || 0} Credits</span>
            </div>
          </div>
          <button onClick={onLogout} className="logout-btn" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#f87171', border: '1px solid rgba(239, 68, 68, 0.2)', padding: '0.5rem 1rem', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
            Logout
          </button>
        </div>
      </header>

      {/* Navigation Menu */}
      <Navigation currentPage={getCurrentPage()} userRole={user?.role} />

      {/* Main Content Area */}
      <main className="tool-content" style={{ padding: '2rem 1.5rem', maxWidth: '1400px', margin: '0 auto' }}>
        <Routes>
          {/* Default Hub Launchpad */}
          <Route 
            path="/" 
            element={<DashboardLaunchpad user={user} />} 
          />

          <Route 
            path="/hub" 
            element={<DashboardLaunchpad user={user} />} 
          />

          <Route 
            path="/niche-research" 
            element={<NicheResearch user={user} onCreditsUpdate={handleUpdateCredits} />} 
          />

          <Route 
            path="/upload-optimizer" 
            element={<UploadOptimizer user={user} onCreditsUpdate={handleUpdateCredits} />} 
          />

          <Route 
            path="/tag-gap" 
            element={<TagGapAnalysis user={user} onCreditsUpdate={handleUpdateCredits} />} 
          />

          <Route 
            path="/revenue-estimator" 
            element={<RevenueEstimator user={user} onCreditsUpdate={handleUpdateCredits} />} 
          />

          <Route 
            path="/video-audit" 
            element={<VideoAuditDashboard user={user} onCreditsUpdate={handleUpdateCredits} />} 
          />
          
          <Route 
            path="/keyword-analysis" 
            element={<AdvancedAnalysis user={user} onCreditsUpdate={handleUpdateCredits} />} 
          />

          <Route 
            path="/comment-mining" 
            element={<CommentMining user={user} onCreditsUpdate={handleUpdateCredits} />} 
          />
          
          <Route 
            path="/pages/competitor" 
            element={<CompetitorAnalysis user={user} onCreditsUpdate={handleUpdateCredits} />} 
          />
          
          <Route 
            path="/pages/trending" 
            element={<TrendingAnalysis user={user} onCreditsUpdate={handleUpdateCredits} />} 
          />

          <Route 
            path="/script-generator" 
            element={<StandaloneScriptGenerator user={user} onCreditsUpdate={handleUpdateCredits} />} 
          />

          <Route 
            path="/history" 
            element={<AnalysisHistory user={user} />} 
          />
          
          <Route 
            path="/admin/observability" 
            element={<AdminObservability user={user} />} 
          />
        </Routes>
      </main>

      {/* Enterprise Footer */}
      <footer className="tool-footer" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', padding: '2rem 1.5rem', textAlign: 'center', color: '#64748b', fontSize: '0.85rem', background: '#090a0f' }}>
        <div className="footer-bottom">
          <p>© 2026 <strong>ContentSERP</strong> - Production Intelligence Platform for YouTube Creators & Marketers</p>
          <p style={{ marginTop: '0.5rem' }}>
            <a href="https://contentserp.in/dashboard" style={{ color: '#94a3b8', textDecoration: 'none', margin: '0 0.5rem' }}>Dashboard</a> |
            <a href="https://contentserp.in/support" style={{ color: '#94a3b8', textDecoration: 'none', margin: '0 0.5rem' }}>Support</a> |
            <a href="https://contentserp.in/blog" style={{ color: '#94a3b8', textDecoration: 'none', margin: '0 0.5rem' }}>Docs & API</a>
          </p>
        </div>
      </footer>
    </div>
  );
}
